import { useCallback, useEffect, useRef, useState } from 'react'
import {
  useStartBlueprintExportMutation,
  useGetExportQuery
} from '../../../../api/coreDataExports'

const POLL_INTERVAL = 2000

const EXPORT_STATES = {
  IDLE: 'idle',
  STARTING: 'starting',
  POLLING: 'polling',
  DOWNLOADING: 'downloading',
  COMPLETE: 'complete',
  ERROR: 'error'
}

function triggerDownload (url) {
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', '')
  document.body.appendChild(link)
  link.click()
  link.remove()
}

/**
 * Orchestrates the full blueprint export lifecycle:
 * start command -> optional polling -> download.
 */
export default function useBlueprintExport () {
  const [exportState, setExportState] = useState(EXPORT_STATES.IDLE)
  const [error, setError] = useState(null)
  const [exportId, setExportId] = useState(null)

  const startMutation = useStartBlueprintExportMutation()

  const isPolling = exportState === EXPORT_STATES.POLLING

  const { data: pollData, error: pollError } = useGetExportQuery(exportId, {
    enabled: isPolling,
    refetchInterval: isPolling ? POLL_INTERVAL : false
  })

  const hasTriggeredDownload = useRef(false)

  useEffect(() => {
    if (!isPolling) return

    if (pollError) {
      setError(pollError?.message || 'Failed to check export status')
      setExportState(EXPORT_STATES.ERROR)
      return
    }

    if (!pollData) return

    if (pollData.status === 'READY' && pollData.downloadUrl && !hasTriggeredDownload.current) {
      hasTriggeredDownload.current = true
      // DOWNLOADING and COMPLETE fire in the same tick -- React batches these,
      // so the "Download starting..." UI message won't render. The DOWNLOADING
      // state exists for state machine completeness, not for visible feedback.
      setExportState(EXPORT_STATES.DOWNLOADING)
      triggerDownload(pollData.downloadUrl)
      setExportState(EXPORT_STATES.COMPLETE)
    }

    if (pollData.status === 'ERROR') {
      setError(pollData.metadata?.errorMessage || 'Export failed')
      setExportState(EXPORT_STATES.ERROR)
    }
  }, [pollData, pollError, isPolling])

  const startExport = useCallback(async (params) => {
    setError(null)
    setExportId(null)
    hasTriggeredDownload.current = false
    setExportState(EXPORT_STATES.STARTING)

    try {
      const result = await startMutation.mutateAsync(params)
      setExportId(result.exportId)
      setExportState(EXPORT_STATES.POLLING)
    } catch (err) {
      setError(err?.message || 'Failed to start export')
      setExportState(EXPORT_STATES.ERROR)
    }
  }, [startMutation])

  const reset = useCallback(() => {
    setExportState(EXPORT_STATES.IDLE)
    setError(null)
    setExportId(null)
    hasTriggeredDownload.current = false
  }, [])

  const isBusy = exportState === EXPORT_STATES.STARTING ||
    exportState === EXPORT_STATES.POLLING ||
    exportState === EXPORT_STATES.DOWNLOADING

  return { startExport, exportState, isBusy, error, reset }
}

export { EXPORT_STATES }
